import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import SEOHead from "./SEOHead";
import logo from "../assets/logo.jpg";
import "bootstrap/dist/css/bootstrap.min.css";
import "./PrivacyPolicy.css";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.07, duration: 0.45 } }),
};

const RIGHTS_TABLE = [
  ["Right to Access", "Request a copy of the personal data we hold about you."],
  ["Right to Rectification", "Request correction of inaccurate or incomplete data."],
  ["Right to Erasure", 'Request deletion of your personal data ("right to be forgotten").'],
  ["Right to Restriction", "Request that we restrict processing of your data."],
  ["Right to Portability", "Receive your data in a structured, machine-readable format."],
  ["Right to Object", "Object to processing of your data for certain purposes."],
];

const Section = ({ number, title, children, index }) => (
  <motion.section
    className="pp-section"
    variants={fadeUp}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    custom={index}
  >
    <h2 className="pp-section-title">
      <span className="pp-section-num">{number}</span>
      {title}
    </h2>
    {children}
  </motion.section>
);

const Sub = ({ title, children }) => (
  <div className="pp-sub">
    <h3 className="pp-sub-title">{title}</h3>
    <p className="pp-sub-body">{children}</p>
  </div>
);

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEOHead
        title="Privacy Policy – ComLinkr"
        description="Learn how ComLinkr collects, uses, and protects your personal information. We never sell your data."
        canonical="https://www.comlinkr.com/privacy"
      />

      {/* Header */}
      <div className="pp-header">
        <img
          src={logo}
          alt="ComLinkr logo"
          className="pp-header-logo"
          onClick={() => navigate("/")}
        />
        <motion.h1
          className="pp-header-title"
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Privacy Policy
        </motion.h1>
        <motion.p
          className="pp-header-sub"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          We're committed to protecting your personal information and being transparent about how we use it.
        </motion.p>
        <span className="pp-badge">Last updated: April 16, 2026</span>
      </div>

      <div className="pp-container">

        {/* Quick Summary */}
        <motion.div
          className="pp-summary"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
        >
          <h2 className="pp-summary-heading">Quick Summary</h2>
          <div className="pp-summary-grid">
            {[
              { color: "green", text: "We never sell your data" },
              { color: "green", text: "End-to-end encryption" },
              { color: "blue",  text: "You control your data" },
              { color: "orange", text: "Delete your account anytime" },
            ].map(({ color, text }) => (
              <div key={text} className="pp-summary-item">
                <span className={`pp-dot pp-dot--${color}`} />
                {text}
              </div>
            ))}
          </div>
        </motion.div>

        <p className="pp-intro">
          This Privacy Policy applies to the ComLinkr mobile application (available on Android and iOS),
          our website at{" "}
          <a href="https://www.comlinkr.com" target="_blank" rel="noopener noreferrer">
            www.comlinkr.com
          </a>
          , and all related services (collectively, the "Service"). By using ComLinkr, you agree to the
          collection and use of information in accordance with this policy.
        </p>

        {/* 1 */}
        <Section number="1." title="Information We Collect" index={0}>
          <Sub title="Account Information">
            When you create an account, we collect your name, email address, city, and optional profile
            details such as your bio, interests, and profile photo.
          </Sub>
          <Sub title="Usage Data">
            We automatically collect information about how you interact with ComLinkr, including pages
            viewed, features used, time spent, and general device information (device type, OS version,
            app version).
          </Sub>
          <Sub title="Location Data">
            We use your selected city to show relevant community content. We do not track your precise GPS
            location unless you explicitly grant location permissions for features like nearby events.
          </Sub>
          <Sub title="Content You Share">
            Posts, comments, messages, listings, and any other content you create or share on ComLinkr is
            stored to provide the Service.
          </Sub>
          <Sub title="Camera &amp; Photos">
            If you choose to upload a profile photo or post images, we request access to your device's
            camera or photo library. This access is only used when you actively choose to upload content.
          </Sub>
        </Section>

        <hr className="pp-hr" />

        {/* 2 */}
        <Section number="2." title="How We Use Your Information" index={1}>
          <Sub title="Provide &amp; Improve Services">
            We use your information to operate ComLinkr, personalise your feed, suggest relevant events
            and listings, and improve our features based on usage patterns.
          </Sub>
          <Sub title="Communications">
            We may send push notifications and emails about activity on your posts, messages, and events
            you're interested in. You can manage notification preferences in Settings. We use Resend
            {" "}(<a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>)
            {" "}to deliver transactional emails such as password resets and account notifications.
          </Sub>
          <Sub title="Safety &amp; Security">
            We use information to detect and prevent fraud, abuse, and violations of our community
            guidelines. This includes automated content moderation.
          </Sub>
          <Sub title="Analytics">
            We use Firebase Analytics (Google) to understand aggregate usage patterns and improve the app
            experience. Firebase Analytics collects anonymised usage data. For more information, see{" "}
            <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">
              Google's Privacy Policy
            </a>.
          </Sub>
        </Section>

        <hr className="pp-hr" />

        {/* 3 */}
        <Section number="3." title="How We Share Your Information" index={2}>
          <Sub title="Community Visibility">
            Your profile, posts, and listings are visible to other ComLinkr members in your community.
            Direct messages are only visible to conversation participants.
          </Sub>
          <Sub title="Service Providers">
            We work with trusted third-party services for hosting, analytics, and notifications. These
            providers are bound by confidentiality agreements and cannot use your data for their own
            purposes.
          </Sub>
          <Sub title="Legal Requirements">
            We may disclose information if required by law, legal process, or to protect the safety and
            rights of ComLinkr, our users, or the public.
          </Sub>
          <Sub title="No Selling of Data">
            We do not sell your personal information to advertisers or any third parties. Your data is
            used solely to power and improve the ComLinkr experience.
          </Sub>
        </Section>

        <hr className="pp-hr" />

        {/* 4 */}
        <Section number="4." title="Data Security" index={3}>
          <Sub title="Encryption">
            All data transmitted between your device and our servers is encrypted using
            industry-standard TLS/SSL protocols. Sensitive data is encrypted at rest.
          </Sub>
          <Sub title="Access Controls">
            We implement strict access controls and authentication measures. Only authorised personnel
            can access user data, and all access is logged and monitored.
          </Sub>
          <Sub title="Regular Audits">
            We conduct regular security audits to identify and address vulnerabilities in our systems.
          </Sub>
        </Section>

        <hr className="pp-hr" />

        {/* 5 */}
        <Section number="5." title="Your Rights &amp; Choices" index={4}>
          <Sub title="Access &amp; Download">
            You can view and download your personal data at any time from Settings → Privacy → Download
            My Data.
          </Sub>
          <Sub title="Edit &amp; Update">
            You can update your profile information, interests, and preferences at any time through your
            profile settings.
          </Sub>
          <Sub title="Delete Account">
            You can permanently delete your account and all associated data from Settings → Account →
            Delete Account. This action is irreversible.
          </Sub>
          <Sub title="Opt Out">
            You can opt out of non-essential communications and personalised content from Settings →
            Privacy.
          </Sub>
        </Section>

        <hr className="pp-hr" />

        {/* 6 */}
        <Section number="6." title="Cookies &amp; Tracking" index={5}>
          <Sub title="Local Storage">
            ComLinkr uses local storage on your device to save preferences, authentication tokens, and
            cached content for a faster experience.
          </Sub>
          <Sub title="Analytics">
            We use Firebase Analytics to understand aggregate usage patterns. Individual users are not
            personally tracked across other apps or websites.
          </Sub>
        </Section>

        <hr className="pp-hr" />

        {/* 7 */}
        <Section number="7." title="Children's Privacy" index={6}>
          <Sub title="Age Requirement">
            ComLinkr is intended for users aged 18 and older. We do not knowingly collect personal
            information from children under 13. If we discover such data has been collected, we will
            delete it promptly. If you believe we have inadvertently collected information from a child
            under 13, please contact us at{" "}
            <a href="mailto:support@comlinkr.com">support@comlinkr.com</a>.
          </Sub>
        </Section>

        <hr className="pp-hr" />

        {/* 8 */}
        <Section number="8." title="GDPR – Rights for EU &amp; UK Users" index={7}>
          <p className="pp-sub-body" style={{ marginBottom: 16 }}>
            If you are located in the European Union or United Kingdom, you have the following rights
            under the General Data Protection Regulation (GDPR) and UK GDPR:
          </p>
          <div className="pp-table-wrap">
            <table className="pp-table">
              <thead>
                <tr>
                  <th>Right</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {RIGHTS_TABLE.map(([right, desc]) => (
                  <tr key={right}>
                    <td><strong>{right}</strong></td>
                    <td>{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="pp-sub-body" style={{ marginTop: 14 }}>
            To exercise any of these rights, contact us at{" "}
            <a href="mailto:support@comlinkr.com">support@comlinkr.com</a>. We will respond within 30
            days. Our legal basis for processing your data is: (a) performance of a contract (providing
            the Service), (b) legitimate interests (security, fraud prevention), and (c) your consent
            (optional features).
          </p>
        </Section>

        <hr className="pp-hr" />

        {/* 9 */}
        <Section number="9." title="CCPA &amp; CPRA – Rights for California Users" index={8}>
          <p className="pp-sub-body" style={{ marginBottom: 16 }}>
            If you are a California resident, you have the following rights under the California Consumer
            Privacy Act (CCPA) as amended by the California Privacy Rights Act (CPRA):
          </p>
          <Sub title="Right to Know">
            You have the right to request information about the categories and specific pieces of personal
            information we have collected about you, the sources, the business purpose, and the third
            parties with whom we share it.
          </Sub>
          <Sub title="Right to Delete">
            You have the right to request that we delete personal information we have collected from you,
            subject to certain exceptions.
          </Sub>
          <Sub title="Right to Opt-Out of Sale">
            We do not sell personal information. You do not need to opt out.
          </Sub>
          <Sub title="Right to Non-Discrimination">
            We will not discriminate against you for exercising any of your CCPA rights.
          </Sub>
          <Sub title="Right to Correct">
            You have the right to request correction of inaccurate personal information we maintain about
            you.
          </Sub>
          <p className="pp-sub-body" style={{ marginTop: 14 }}>
            To submit a CCPA request, contact us at{" "}
            <a href="mailto:support@comlinkr.com">support@comlinkr.com</a>.
          </p>
        </Section>

        <hr className="pp-hr" />

        {/* 10 */}
        <Section number="10." title="CalOPPA – California Online Privacy Protection Act" index={9}>
          <p className="pp-sub-body">
            In accordance with CalOPPA, we agree to the following: users can visit our app and website
            anonymously; this Privacy Policy link is clearly accessible; our Privacy Policy link includes
            the word "Privacy" and is easily findable; users will be notified of any Privacy Policy
            changes on this page; users are able to change their personal information by logging into
            their account or contacting us.
          </p>
          <p className="pp-sub-body" style={{ marginTop: 12 }}>
            <strong>Do Not Track:</strong> We do not respond to Do Not Track (DNT) browser signals as
            there is no industry-standard approach to DNT compliance at this time.
          </p>
        </Section>

        <hr className="pp-hr" />

        {/* 11 */}
        <Section number="11." title="Changes to This Policy" index={10}>
          <p className="pp-sub-body">
            We may update this Privacy Policy from time to time. We will notify you of significant
            changes through the app or via email. The "Last updated" date at the top of this page will
            always reflect the most recent revision. Continued use of ComLinkr after changes constitutes
            acceptance of the updated policy.
          </p>
        </Section>

        <hr className="pp-hr" />

        {/* Contact */}
        <motion.div
          className="pp-contact"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={11}
        >
          <h2 className="pp-contact-title">Questions about your privacy?</h2>
          <p className="pp-contact-sub">Contact our privacy team — we're happy to help.</p>
          <a href="mailto:support@comlinkr.com" className="pp-contact-btn">
            Email Us: support@comlinkr.com
          </a>
          <p className="pp-contact-domain">ComLinkr · www.comlinkr.com</p>
        </motion.div>

        <div className="pp-footer">
          <p>© 2026 ComLinkr. All rights reserved.</p>
          <p>This policy applies to all ComLinkr services.</p>
        </div>

      </div>
    </>
  );
};

export default PrivacyPolicy;
